import React, { useState, useEffect } from 'react';
import { TagIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { categoryService, Category } from '../../services/categoryService';
import { tagService, Tag } from '../../services/tagService';
import { documentService } from '../../services/documentService';
import { useToast } from '../UI/ToastProvider';

interface DocumentCategoriesAndTagsProps {
  documentId: string;
  currentCategory?: string;
  currentCategories?: string[];
  currentTags?: string[];
  onCategoryChange: (category: string) => void;
  onCategoriesChange: (categories: string[]) => void;
  onTagsChange: (tags: string[]) => void;
  showOnlyCategories?: boolean;
  showOnlyTags?: boolean;
}

export const DocumentCategoriesAndTags: React.FC<DocumentCategoriesAndTagsProps> = ({
  documentId,
  currentCategory,
  currentCategories = [],
  currentTags = [],
  onCategoryChange,
  onCategoriesChange,
  onTagsChange,
  showOnlyCategories = false,
  showOnlyTags = false,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingTags, setIsLoadingTags] = useState(true);
  const [showTagInput, setShowTagInput] = useState(false);
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [isLoadingCategory, setIsLoadingCategory] = useState(false);
  const [isLoadingTag, setIsLoadingTag] = useState(false);
  const [filteredTags, setFilteredTags] = useState<Tag[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const { addToast } = useToast();

  // Carregar categorias
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const data = await categoryService.getAllCategories();
        setCategories(data);
        setFilteredCategories(data);
      } catch (error) {
        addToast('Erro ao carregar categorias', 'error');
      } finally {
        setIsLoadingCategories(false);
      }
    };

    loadCategories();
  }, [addToast]);

  // Carregar tags
  useEffect(() => {
    const loadTags = async () => {
      try {
        setIsLoadingTags(true);
        const data = await tagService.getAllTags();
        setTags(data);
        setFilteredTags(data);
      } catch (error) {
        addToast('Erro ao carregar tags', 'error');
      } finally {
        setIsLoadingTags(false);
      }
    };

    loadTags();
  }, [addToast]);

  // Filtrar tags baseado no input
  useEffect(() => {
    if (newTagName.trim()) {
      const filtered = tags.filter(tag =>
        tag.name.toLowerCase().includes(newTagName.toLowerCase())
      );
      setFilteredTags(filtered);
    } else {
      setFilteredTags(tags);
    }
  }, [newTagName, tags]);

  // Filtrar categorias baseado no input
  useEffect(() => {
    if (newCategoryName.trim()) {
      const filtered = categories.filter(category =>
        category.name.toLowerCase().includes(newCategoryName.toLowerCase())
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(categories);
    }
  }, [newCategoryName, categories]);

  const handleCategorySelect = async (category: Category) => {
    if (!currentCategories.includes(category.name)) {
      setIsLoadingCategory(true);
      try {
        await documentService.addCategoryToDocument(documentId, category.id);
        const updatedCategories = [...currentCategories, category.name];
        onCategoriesChange(updatedCategories);
        addToast('Categoria adicionada com sucesso!', 'success');
      } catch (error) {
        addToast('Erro ao adicionar categoria. Tente novamente.', 'error');
      } finally {
        setIsLoadingCategory(false);
      }
    }
    setShowCategoryInput(false);
    setNewCategoryName('');
  };

  const handleCategoryInputChange = (value: string) => {
    setNewCategoryName(value);
  };

  const handleTagInputChange = (value: string) => {
    // Converter maiúsculas para minúsculas
    let cleanValue = value.toLowerCase();
    
    // Substituir espaços por hífens
    cleanValue = cleanValue.replace(/\s+/g, '-');
    
    // Remover caracteres especiais, mantendo apenas letras, números e hífens
    cleanValue = cleanValue.replace(/[^a-z0-9-]/g, '');
    
    // Remover hífens duplicados
    cleanValue = cleanValue.replace(/-+/g, '-');
    
    // Remover hífens no início e fim
    cleanValue = cleanValue.replace(/^-+|-+$/g, '');
    
    setNewTagName(cleanValue);
  };

  const handleTagSelect = async (tag: Tag) => {
    if (!currentTags.includes(tag.name)) {
      setIsLoadingTag(true);
      try {
        await documentService.addTagToDocument(documentId, tag.id);
        const updatedTags = [...currentTags, tag.name];
        onTagsChange(updatedTags);
        addToast('Tag adicionada com sucesso!', 'success');
      } catch (error) {
        addToast('Erro ao adicionar tag. Tente novamente.', 'error');
      } finally {
        setIsLoadingTag(false);
      }
    }
    setShowTagInput(false);
    setNewTagName('');
  };

  const handleCreateNewTag = async () => {
    if (!newTagName.trim() || newTagName.length < 2) {
      addToast('Nome da tag deve ter pelo menos 2 caracteres', 'error');
      return;
    }

    setIsCreatingTag(true);
    try {
      const newTag = await tagService.createTag({
        name: newTagName,
        type: 'document'
      });
      
      // Adicionar a nova tag à lista
      setTags(prev => [...prev, newTag]);
      
      // Adicionar ao documento
      if (!currentTags.includes(newTag.name)) {
        await documentService.addTagToDocument(documentId, newTag.id);
        const updatedTags = [...currentTags, newTag.name];
        onTagsChange(updatedTags);
        addToast('Tag criada e adicionada com sucesso!', 'success');
      }
      
      setShowTagInput(false);
      setNewTagName('');
    } catch (error) {
      addToast('Erro ao criar tag. Tente novamente.', 'error');
    } finally {
      setIsCreatingTag(false);
    }
  };

  const handleRemoveCategory = async (categoryToRemove: string) => {
    const category = categories.find(c => c.name === categoryToRemove);
    if (!category) return;

    setIsLoadingCategory(true);
    try {
      await documentService.removeCategoryFromDocument(documentId, category.id);
      const updatedCategories = currentCategories.filter(category => category !== categoryToRemove);
      onCategoriesChange(updatedCategories);
      addToast('Categoria removida com sucesso!', 'success');
    } catch (error) {
      addToast('Erro ao remover categoria. Tente novamente.', 'error');
    } finally {
      setIsLoadingCategory(false);
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const tag = tags.find(t => t.name === tagToRemove);
    if (!tag) return;

    setIsLoadingTag(true);
    try {
      await documentService.removeTagFromDocument(documentId, tag.id);
      const updatedTags = currentTags.filter(tag => tag !== tagToRemove);
      onTagsChange(updatedTags);
      addToast('Tag removida com sucesso!', 'success');
    } catch (error) {
      addToast('Erro ao remover tag. Tente novamente.', 'error');
    } finally {
      setIsLoadingTag(false);
    }
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredTags.length > 0) {
        handleTagSelect(filteredTags[0]);
      } else {
        handleCreateNewTag();
      }
    } else if (e.key === 'Escape') {
      setShowTagInput(false);
      setNewTagName('');
    }
  };

  const handleCategoryKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCategories.length > 0) {
        handleCategorySelect(filteredCategories[0]);
      }
    } else if (e.key === 'Escape') {
      setShowCategoryInput(false);
      setNewCategoryName('');
    }
  };

  return (
    <div className="space-y-4">
      {/* Categorias - Mostrar apenas se não for showOnlyTags */}
      {!showOnlyTags && (
        <div>
          {/* Categorias existentes */}
          {currentCategories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {currentCategories.map((category) => (
                <span
                  key={category}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                >
                  {category}
                  <button
                    onClick={() => handleRemoveCategory(category)}
                    disabled={isLoadingCategory}
                    className="ml-1 hover:text-green-600 disabled:opacity-50"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input para selecionar categoria */}
          {showCategoryInput ? (
            <div className="relative">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => handleCategoryInputChange(e.target.value)}
                onKeyDown={handleCategoryKeyPress}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Buscar categoria..."
                autoFocus
              />
              
              {/* Dropdown de sugestões */}
              {newCategoryName.trim() && filteredCategories.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Botão de cancelar */}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => {
                    setShowCategoryInput(false);
                    setNewCategoryName('');
                  }}
                  className="px-3 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowCategoryInput(true)}
              disabled={isLoadingCategory}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Adicionar Categoria
            </button>
          )}
        </div>
      )}

      {/* Tags - Mostrar apenas se não for showOnlyCategories */}
      {!showOnlyCategories && (
        <div>
          {/* Tags existentes */}
          {currentTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {currentTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  #{tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    disabled={isLoadingTag}
                    className="ml-1 hover:text-blue-600 disabled:opacity-50"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input para adicionar nova tag */}
          {showTagInput ? (
            <div className="relative">
              <div className="flex">
                <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-gray-500 text-sm">
                  #
                </span>
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => handleTagInputChange(e.target.value)}
                  onKeyDown={handleTagKeyPress}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="nome-da-tag"
                  autoFocus
                />
              </div>
              
              {/* Dropdown de sugestões */}
              {newTagName.trim() && filteredTags.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredTags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleTagSelect(tag)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                    >
                      #{tag.name}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Botões de ação */}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleCreateNewTag}
                  disabled={isCreatingTag || !newTagName.trim() || newTagName.length < 2}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {isCreatingTag ? 'Criando...' : 'Criar Tag'}
                </button>
                <button
                  onClick={() => {
                    setShowTagInput(false);
                    setNewTagName('');
                  }}
                  className="px-3 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowTagInput(true)}
              disabled={isLoadingTag}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              <TagIcon className="h-4 w-4 mr-2" />
              Adicionar Tag
            </button>
          )}
        </div>
      )}
    </div>
  );
}; 